import { useAuth } from "@via/hooks/useAuth";

import { EmailRegisterForm } from "@via/pages/authentication/self-service-signup/email-register-form.tsx";
import GuestSignupProgress from "@via/pages/authentication/guest-signup/progress/guest-signup-progress.tsx";
import { ROUTES } from "@via/utils/constants.tsx";
import { signUpOptions } from "@via/utils/form-validators.tsx";
import { RegisterRequestBody } from "common";
import { type JSX, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  useNavigate,
  useSearchParams,
  Navigate,
  useLocation,
} from "react-router-dom";
import { shouldBlockAccess } from "@via/utils/helpers";
import { useGetUserInvitationCheck } from "@via/pages/account/admin/admin.api.tsx";
import { useNotifications } from "@via/hooks/useNotifications";
import { NOTIFICATION } from "@via/context/notifications-context";
import { ErrorNotification } from "@via/utils/interfaces";

enum GuestSignupComponentView {
  RegisterForm = 1,
  SignupProgress = 3,
}

export default function GuestSignUp(): JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const userEmail = searchParams.get("email");
  const { pathname } = useLocation();

  const { setNotification } = useNotifications();
  const { user } = useAuth();
  const navigate = useNavigate();

  // If you're already logged in, just redirect to "/activity"
  if (user?.session && pathname === ROUTES.GUEST_SIGNUP) {
    // Additional logic for self-registered/invited users not fully onboarded
    const blockAccess = shouldBlockAccess(user);
    if (!blockAccess) {
      return <Navigate to={ROUTES.ACTIVITY} replace />;
    }
  }

  // For guest signup, token is required - redirect if not present
  useEffect(() => {
    if (!token) {
      setNotification({
        id: "guest-signup-no-token",
        message: "Invalid Invitation",
        details:
          "This invitation link is invalid or has expired. Please contact the person who invited you.",
        variant: NOTIFICATION.ERROR,
      });
      navigate(ROUTES.LOGIN);
      return;
    }
  }, [token, navigate, setNotification]);

  const {
    data: invitationDetails,
    isSuccess: isInvitationDetailsSuccess,
    isError: isInvitationDetailsError,
    error: invitationDetailsError,
  } = useGetUserInvitationCheck({ token: token || "" });
  const [componentView, setComponentView] = useState<GuestSignupComponentView>(
    GuestSignupComponentView.RegisterForm
  );
  const {
    watch,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<RegisterRequestBody>({
    mode: "onChange",
    defaultValues: {
      email: userEmail || "",
    },
  });

  useEffect(() => {
    if (invitationDetails?.invitedEmail) {
      setValue("email", invitationDetails.invitedEmail);
    }
  }, [isInvitationDetailsSuccess, invitationDetails]);

  useEffect(() => {
    if (isInvitationDetailsError && invitationDetailsError) {
      setNotification({
        id: "invitation-check-error",
        message: "Invitation failed",
        details:
          (invitationDetailsError as ErrorNotification)?.response?.data
            ?.message || "An error occurred while checking the invitation.",
        variant: NOTIFICATION.ERROR,
      });
      navigate(ROUTES.LOGIN);
    }
  }, [
    isInvitationDetailsError,
    invitationDetailsError,
    navigate,
    setNotification,
  ]);

  const handleEmailRegister = () => {
    setComponentView(GuestSignupComponentView.SignupProgress);
  };

  const emailValue = watch("email");

  return (
    <>
      <div className="flex flex-1 flex-col py-6 sm:px-8 lg:px-8">
        <div className="mx-8 lg:mx-24 flex h-full flex-row items-center justify-center">
          <div className="flex flex-col items-center justify-center">
            {componentView === GuestSignupComponentView.RegisterForm ? (
              <EmailRegisterForm
                register={register}
                errors={errors}
                isRegisterEmailLoading={false}
                onSubmit={handleSubmit(handleEmailRegister)}
                signUpOptions={signUpOptions}
                invitationDetails={invitationDetails}
              />
            ) : componentView === GuestSignupComponentView.SignupProgress ? (
              <GuestSignupProgress email={user?.email || emailValue} />
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}
